import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { NumericFormat } from "react-number-format";
import AddIcon from "@mui/icons-material/Add";
import { DealBuilderSavedFormat } from "../../hooks/useDatabaseAPI";

interface InputBidResultsEditorProps {
  deal: DealBuilderSavedFormat;
  onSave: (deal: DealBuilderSavedFormat) => void;
  onClose: () => void;
}

export default function InputBidResultsEditor({ deal, onSave, onClose }: InputBidResultsEditorProps) {
  const [newDeal, setNewDeal] = useState({ ...deal });

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>Input Bid Results</DialogTitle>
      <DialogContent sx={{ minWidth: "400px" }}>
        <Stack direction="column" gap={1.5} py={2}>

          <TextField
            label="Borrower"
            value={newDeal.borrower}
            sx={{ width: "100%" }}
            onChange={(e) => {
              const borrower = e.target.value;
              setNewDeal({ ...newDeal, borrower });
            }}
          />

          <TextField
            label="Issuance Title"
            value={newDeal.issuance_title}
            sx={{ width: "100%" }}
            onChange={(e) => {
              const issuance_title = e.target.value;
              setNewDeal({ ...newDeal, issuance_title });
            }}
          />

          <Stack direction="row" gap={1}>
            <DatePicker
              label="Bid Date"
              format="MM/DD/YYYY"
              value={moment(newDeal.bid_date)}
              slotProps={{ textField: { sx: { width: '100%' }} }}
              onChange={(value) =>
                value &&
                value.isValid() &&
                setNewDeal({
                  ...newDeal,
                  bid_date: value.toISOString(),
                })
              }
            />

            <DatePicker
              label="Closing Date"
              format="MM/DD/YYYY"
              value={moment(newDeal.delivery_date)}
              slotProps={{ textField: { sx: { width: '100%' }} }}
              onChange={(value) =>
                value &&
                value.isValid() &&
                setNewDeal({
                  ...newDeal,
                  delivery_date: value.toISOString(),
                })
              }
            />
          </Stack>
          
          <NumericFormat
            label="Bid Rate"
            value={newDeal.bid_rate * 100}
            customInput={TextField}
            decimalScale={3}
            thousandSeparator=","
            variant="outlined"
            onValueChange={(values) =>
              setNewDeal({
                ...newDeal,
                bid_rate: (values.floatValue || 0) / 100,
              })
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            sx={{ width: "100%" }}
          />

          <TextField
            label="Advisor"
            value={newDeal.advisor}
            sx={{ width: "100%" }}
            onChange={(e) => {
              const advisor = e.target.value;
              setNewDeal({ ...newDeal, advisor });
            }}
          />

          <Divider />

          {newDeal.other_bids.map((data, index) => {
            return (
              <Stack key={index} direction="row" gap={1}>
                <TextField
                  label="Bid name"
                  value={data[0]}
                  sx={{ width: "100%" }}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const newBidList = [...newDeal.other_bids];
                    newBidList[index][0] = newValue;
                    setNewDeal({
                      ...newDeal,
                      other_bids: newBidList,
                    });
                  }}
                />
                <NumericFormat
                  label="Bid Rate"
                  value={newDeal.other_bids[index][1] * 100}
                  customInput={TextField}
                  decimalScale={3}
                  thousandSeparator=","
                  variant="outlined"
                  onValueChange={(values) => {
                    const newValue = (values.floatValue || 0) / 100;
                    const newBidList = [...newDeal.other_bids];
                    newBidList[index][1] = newValue;
                    setNewDeal({
                      ...newDeal,
                      other_bids: newBidList,
                    });
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  sx={{ width: "100%" }}
                />
              </Stack>
            );
          })}

          <Stack
            direction="row"
            onClick={() =>
              setNewDeal({
                ...newDeal,
                other_bids: [...newDeal.other_bids, ["", 0.04]],
              })
            }
            gap={0.5}
            sx={{
              cursor: "pointer",
              opacity: 1,
              "&:hover": { opacity: 0.75 },
            }}
          >
            <AddIcon fontSize="small" color="info" />
            <Typography variant="subtitle2">Add another bid</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave(newDeal)}>Update</Button>
      </DialogActions>
    </Dialog>
  );
}
